import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { Job_Read } from '@treadinc/horizon-api-spec';
import { useEffect, useId, useRef } from 'react';

import { realTimeChannels } from '~services/realTimeChannels';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

/**
 * Invalidate react-query keys when a `CompanyJobUpdateChannel` push arrives for one of `jobIds`.
 *
 * Load-level pricing panels are plain react-query views that go stale after an edit, because a
 * cascade/divergence re-rates the Pricing rows server-side ASYNC in the worker: a post-mutation
 * `invalidateQueries` races the worker (refetches before the new rows land), and `refetchOnWindowFocus`
 * only fires on a window blur+refocus — so the UI stays stale until a hard refresh. The backend pushes
 * every affected job over `CompanyJobUpdateChannel` AFTER the worker finishes (the cascade's
 * `broadcast_rate_change_to_dispatch!` and the per-job rate pass's legacy `update!` callbacks), so that
 * push is the race-free signal to refetch.
 *
 * Deliberately NOT gated on any feature flag: react-query invalidation is a silent background refetch,
 * not the flashy full reload the dispatch `dispatch_smart_auto_refresh` flag guards against.
 *
 * The channel pushes a `{ data: Job_Read }` message (see useOrdersDispatch); we match the job id and
 * ignore pushes for other jobs so a busy company board doesn't refetch this panel on every update.
 */
export default function useInvalidateQueriesOnJobUpdate(
  companyId: string | null | undefined,
  jobIds: Array<string | null | undefined>,
  queryKeys: QueryKey[],
): void {
  const queryClient = useQueryClient();
  const uniqueKey = useId();

  // Read the latest jobIds/queryKeys from refs so the subscription is set up once per companyId and
  // does not churn when the parent re-renders with new array identities.
  const jobIdsRef = useRef(jobIds);
  const queryKeysRef = useRef(queryKeys);
  jobIdsRef.current = jobIds;
  queryKeysRef.current = queryKeys;

  useEffect(() => {
    if (!companyId) {
      return undefined;
    }

    const handler = (resp: { data?: Job_Read } | undefined) => {
      const jobId = resp?.data?.id;
      if (!jobId || !jobIdsRef.current.includes(jobId)) {
        return;
      }

      queryKeysRef.current.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    };

    subscribeWithMultipleHandlers(
      realTimeChannels.CompanyJobUpdateChannel,
      companyId,
      uniqueKey,
      handler,
    );

    return () => {
      unsubscribeHandler(realTimeChannels.CompanyJobUpdateChannel, companyId, uniqueKey);
    };
  }, [companyId, uniqueKey, queryClient]);
}
